import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ListingMapbox } from '@/components/Listing/Map/ListingMapbox';
import { ListingCards } from '@/components/Listing/Cards';
import { SearchBarForm } from '@/components/SearchBarForm';
import { ListingContainerTabs } from '@/components/Listing/Container/Tabs';
import { ListingWithFavorited } from '@/types';
import { setSearchParamsInUrl } from '@/components/helpers/queryParam';

interface ListingContainerProps {
  signedIn: boolean;
  school: any;
  pois: any[];
  searchParams: any;
}

export function ListingContainer({ signedIn, school, pois, searchParams }: ListingContainerProps) {
  const [loading, setLoading] = useState(true);
  // Distinguishes the very first fetch (show a full-area spinner) from later
  // searches (keep results mounted, overlay a spinner) so re-searching doesn't
  // blank the page.
  const [hasLoaded, setHasLoaded] = useState(false);
  // Bumped on every completed fetch to force the marker cluster layer to
  // re-render with the new results while the map itself stays mounted.
  const [searchVersion, setSearchVersion] = useState(0);
  const [listingsArr, setListingsArr] = useState<ListingWithFavorited[]>([]);
  const [listingsObjByLongLat, setListingsObjByLongLat] = useState({});
  const [listView, setListView] = useState(true);

  // favorites/blocks are composite "Type:id" keys, e.g. "Listing:42", so the
  // same endpoints serve every postable type without a shape change.
  const parseListings = (listings: ListingWithFavorited[], favorites: string[], blocks: string[]) => {
    const favoriteKeys = new Set(favorites);
    const blockKeys = new Set(blocks);
    // Object for Map View = {
    //  "longitude, latitude string 1": [{listing1}, {listing2}],
    //  "longitude, latitude string 2": [{listing3}]
    // }
    let newListingsObjByLngLat: Record<string, ListingWithFavorited[]> = {};

    // Array for List View = [{listing1}, {listing2}, {listing3}]
    let newListingsArr = [];

    for (let listing of listings) {
      if (blockKeys.has(`Listing:${listing.id}`)) {
        continue;
      }

      const longLatStr = `${listing.longitude}, ${listing.latitude}`;

      listing.favorited = favoriteKeys.has(`Listing:${listing.id}`);

      newListingsArr.push(listing);

      if (longLatStr in newListingsObjByLngLat) {
        newListingsObjByLngLat[longLatStr].push(listing);
      } else {
        newListingsObjByLngLat[longLatStr] = [listing];
      }
    }

    setLoading(false);
    setHasLoaded(true);
    setSearchVersion((v) => v + 1);
    setListingsArr(newListingsArr);
    setListingsObjByLongLat(newListingsObjByLngLat);
  };

  const getListings = async (formState: any) => {
    setLoading(true);
    setSearchParamsInUrl(formState);
    let [listingsResp, favoritesResp, blocksResp] = await Promise.all([
      axios.get(`/api/listings`, { headers: { 'Content-Type': 'application/json' }, params: formState }),
      axios.get(`/api/favorites`, { headers: { 'Content-Type': 'application/json' } }),
      axios.get(`/api/blocks`, { headers: { 'Content-Type': 'application/json' } }),
    ]);
    parseListings(listingsResp.data, favoritesResp.data, blocksResp.data);
  };

  useEffect(() => {
    getListings(searchParams);
  }, []);

  return (
    <div className="map-listings">
      <SearchBarForm listView={listView} searchParams={searchParams} submitHandler={getListings} />

      {!hasLoaded ? (
        // First load: nothing to show yet, so a full-area spinner is fine.
        <p className="lead text-center pt-5">
          <i className="fa fa-circle-o-notch fa-spin fs-2 mb-2" />
          <br />
          Loading...
        </p>
      ) : (
        // Subsequent searches: keep the results and map mounted and overlay a
        // spinner instead of swapping the whole area out for a spinner.
        // Unmounting collapsed the page height (which clamped the scroll
        // position to the top) and forced the Leaflet map to re-initialize on
        // every search.
        <div style={{ position: 'relative' }}>
          {loading && (
            <div
              className="listings-loading-spinner"
              style={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 1000,
                background: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '0.5rem',
                padding: '0.75rem 1.25rem',
                boxShadow: '0 2px 10px rgba(0, 0, 0, 0.15)',
              }}
              role="status"
              aria-live="polite"
            >
              <i className="fa fa-circle-o-notch fa-spin fs-2" />
              <span className="sr-only">Loading…</span>
            </div>
          )}

          <div
            style={{
              opacity: loading ? 0.4 : 1,
              transition: 'opacity 0.15s ease',
              pointerEvents: loading ? 'none' : 'auto',
            }}
          >
            <ListingContainerTabs listView={listView} setListView={setListView} />

            <div style={{ display: listView ? 'inline' : 'none' }}>
              <ListingCards
                signedIn={signedIn}
                listingsArr={listingsArr}
                emptyMessage="Sorry, no listings match those criteria."
              />
            </div>

            {/* requires visibility instead of display to render map properly
            https://stackoverflow.com/questions/11098700/fitbounds-shows-whole-earth-if-map-is-first-hidden-and-then-shown */}
            <div style={{ visibility: listView ? 'hidden' : 'visible' }}>
              <ListingMapbox
                listings={listingsObjByLongLat}
                schoolLatLong={{ lat: school.latitude, lng: school.longitude }}
                pois={pois}
                refreshKey={searchVersion}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
